/**
 * F606OrdersAdmin — full lifecycle order management
 *
 * The 5 legacy fitness card pages are replaced by ONE proper admin table
 * where every row exposes ALL the actions the 606 bridge supports:
 *   • Confirm (only when status ∈ {pending, paid})
 *   • Ship    (only when status = confirmed)
 *   • Deliver (only when status = shipped)
 *   • Cancel + optional refund (any status ≠ delivered/cancelled)
 *   • Add note (any status)
 *
 * All buttons fire the real REST bridge and show which side-effects
 * actually happened (email/push/webhook) via a nested toast.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Check, Truck, PackageCheck, XCircle, MessageSquarePlus, Search } from 'lucide-react';
import { bridge, describeActions } from './bridgeClient';

const STATUS_META = {
  pending:    { label_ar: 'قيد الانتظار', label_en: 'Pending',    color: 'bg-amber-500/15 border-amber-500/40 text-amber-300' },
  paid:       { label_ar: 'مدفوع',       label_en: 'Paid',       color: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' },
  mock_paid:  { label_ar: 'مدفوع (تجريبي)', label_en: 'Paid (mock)', color: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' },
  confirmed:  { label_ar: 'مؤكّد',       label_en: 'Confirmed',  color: 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300' },
  shipped:    { label_ar: 'مشحون',       label_en: 'Shipped',    color: 'bg-blue-500/15 border-blue-500/40 text-blue-300' },
  delivered:  { label_ar: 'تم التوصيل',  label_en: 'Delivered',  color: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' },
  cancelled:  { label_ar: 'ملغي',        label_en: 'Cancelled',  color: 'bg-red-500/15 border-red-500/40 text-red-300' },
  refunded:   { label_ar: 'مسترد',       label_en: 'Refunded',   color: 'bg-neutral-500/15 border-neutral-500/40 text-neutral-300' },
};

const canConfirm = (s) => ['pending', 'paid', 'mock_paid'].includes(s);
const canShip    = (s) => s === 'confirmed';
const canDeliver = (s) => s === 'shipped';
const canCancel  = (s) => !['delivered', 'cancelled', 'refunded'].includes(s);

export default function F606OrdersAdmin({ language }) {
  const isAr = language === 'ar';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});   // { [order_id]: 'confirming' }
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await bridge.list('orders', { limit: 100, search: search || undefined });
    if (res.success !== false) setOrders(res.data || []);
    else toast.error(res.error?.message || 'Failed to load orders');
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const act = async (order, fn, label, extraBody = {}) => {
    setBusy((s) => ({ ...s, [order.id]: label }));
    const res = await fn(order.id, extraBody);
    setBusy((s) => ({ ...s, [order.id]: null }));
    if (res.success) {
      toast.success(
        `✅ ${label} · ${describeActions(res.actions_fired, language)}`,
        { duration: 5000 },
      );
      load();
    } else {
      toast.error(`⚠️ ${res.error?.message || 'Failed'}`);
    }
  };

  const filtered = orders.filter((o) => statusFilter === 'all' || o.status === statusFilter);

  return (
    <div className="space-y-3" data-testid="f606-orders-admin">
      {/* Controls */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute top-2.5 start-3 h-4 w-4 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAr ? 'ابحث برقم الطلب / اسم العميل...' : 'Search by order # / buyer...'}
            className="w-full ps-9 pe-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder:text-white/30"
            data-testid="f606-orders-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm"
          data-testid="f606-orders-status-filter"
        >
          <option value="all">{isAr ? 'كل الحالات' : 'All statuses'}</option>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <option key={k} value={k}>{isAr ? m.label_ar : m.label_en}</option>
          ))}
        </select>
        <button
          onClick={load}
          className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
          data-testid="f606-orders-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/5 text-white/50 text-[10px] uppercase tracking-wider">
              <th className="px-3 py-2 text-start">{isAr ? 'الطلب' : 'Order'}</th>
              <th className="px-3 py-2 text-start">{isAr ? 'العميل' : 'Buyer'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'المبلغ' : 'Total'}</th>
              <th className="px-3 py-2 text-center">{isAr ? 'الحالة' : 'Status'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'الإجراءات' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && orders.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline text-emerald-400/50" /></td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8 text-white/40">{isAr ? 'لا توجد طلبات' : 'No orders'}</td></tr>
            )}
            {filtered.map((o) => {
              const s = String(o.status || '').toLowerCase();
              const meta = STATUS_META[s] || { label_ar: s, label_en: s, color: 'bg-white/5 border-white/10 text-white/70' };
              const bId = busy[o.id];
              return (
                <tr key={o.id} className="hover:bg-white/[0.02]" data-testid={`f606-order-row-${o.id}`}>
                  <td className="px-3 py-2 font-mono text-emerald-300 text-[11px]" dir="ltr">{o.order_number || o.id?.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-white font-mono text-[11px]" dir="ltr">{o.buyer_name || (o.user_id ? o.user_id.slice(0, 8) : '—')}</td>
                  <td className="px-3 py-2 text-end text-white font-mono" dir="ltr">
                    {(o.total ?? o.total_aed) != null ? `${o.total ?? o.total_aed} ${o.currency || 'AED'}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border ${meta.color}`}>
                      {isAr ? meta.label_ar : meta.label_en}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {canConfirm(s) && (
                        <button
                          disabled={bId}
                          onClick={() => act(o, bridge.order.confirm, isAr ? 'تأكيد' : 'Confirm')}
                          className="px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-order-confirm-${o.id}`}
                        >
                          <Check className="h-3 w-3 inline" /> {isAr ? 'تأكيد' : 'Confirm'}
                        </button>
                      )}
                      {canShip(s) && (
                        <button
                          disabled={bId}
                          onClick={() => {
                            const tracking = window.prompt(isAr ? 'رقم التتبّع (اختياري):' : 'Tracking number (optional):') || '';
                            const carrier = tracking ? (window.prompt(isAr ? 'شركة الشحن:' : 'Carrier:') || '') : '';
                            act(o, bridge.order.ship, isAr ? 'شحن' : 'Ship', { tracking_number: tracking, carrier });
                          }}
                          className="px-2 py-1 rounded-md bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-order-ship-${o.id}`}
                        >
                          <Truck className="h-3 w-3 inline" /> {isAr ? 'شحن' : 'Ship'}
                        </button>
                      )}
                      {canDeliver(s) && (
                        <button
                          disabled={bId}
                          onClick={() => act(o, bridge.order.deliver, isAr ? 'تسليم' : 'Deliver')}
                          className="px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-order-deliver-${o.id}`}
                        >
                          <PackageCheck className="h-3 w-3 inline" /> {isAr ? 'تسليم' : 'Deliver'}
                        </button>
                      )}
                      {canCancel(s) && (
                        <button
                          disabled={bId}
                          onClick={() => {
                            const reason = window.prompt(isAr ? 'سبب الإلغاء:' : 'Cancel reason:') || '';
                            if (reason === null) return;
                            const refund = window.confirm(isAr ? 'إصدار استرداد؟' : 'Issue refund?');
                            act(o, bridge.order.cancel, isAr ? 'إلغاء' : 'Cancel', { reason, refund });
                          }}
                          className="px-2 py-1 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 hover:bg-red-500/20 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-order-cancel-${o.id}`}
                        >
                          <XCircle className="h-3 w-3 inline" /> {isAr ? 'إلغاء' : 'Cancel'}
                        </button>
                      )}
                      <button
                        disabled={bId}
                        onClick={() => {
                          const content = window.prompt(isAr ? 'الملاحظة:' : 'Note content:') || '';
                          if (!content) return;
                          act(o, bridge.order.addNote, isAr ? 'ملاحظة' : 'Note', { content, visible_to: ['admin_only'] });
                        }}
                        className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 text-[10px] disabled:opacity-40"
                        data-testid={`f606-order-note-${o.id}`}
                      >
                        <MessageSquarePlus className="h-3 w-3 inline" /> {isAr ? 'ملاحظة' : 'Note'}
                      </button>
                      {bId && <Loader2 className="h-3 w-3 animate-spin text-emerald-400/70" />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
